import React, { useEffect, useState } from "react";
import { Modal, Button, Accordion, Card } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { modeConstants } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { userActions } from "../../redux/actions";
import PhoneInput from "react-phone-input-2";

// --- Schema ---
// Profile fields are light validation here.
// Passwords: only required if user is changing password.
// If any of current/new/confirm is provided, we require all three and ensure match & min length.
const schema = z
  .object({
    name: z.string().min(2, "The field is required"),
    contactNumber: z.string().min(1, "The field is required"),
    country: z.string().min(1, "The field is required"),
    state: z.string().min(1, "The field is required"),
    city: z.string().min(1, "The field is required"),
    location: z.string().min(1, "The field is required"),
    gender: z.string().min(1, "The field is required"),
    nationality: z.string().min(1, "The field is required"),
    pic: z.any().optional(),

    // change password section
    currentPassword: z.string().optional(),
    newPassword: z.string().optional(),
    confirmPassword: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    const anyPasswordEntered =
      (val.currentPassword && val.currentPassword.length > 0) ||
      (val.newPassword && val.newPassword.length > 0) ||
      (val.confirmPassword && val.confirmPassword.length > 0);

    if (anyPasswordEntered) {
      if (!val.currentPassword) {
        ctx.addIssue({
          path: ["currentPassword"],
          code: z.ZodIssueCode.custom,
          message: "Current password is required.",
        });
      }
      if (!val.newPassword || val.newPassword.length < 6) {
        ctx.addIssue({
          path: ["newPassword"],
          code: z.ZodIssueCode.custom,
          message: "New password must be at least 6 characters.",
        });
      }
      if (!val.confirmPassword) {
        ctx.addIssue({
          path: ["confirmPassword"],
          code: z.ZodIssueCode.custom,
          message: "Please confirm the new password.",
        });
      }
      if (
        val.newPassword &&
        val.confirmPassword &&
        val.newPassword !== val.confirmPassword
      ) {
        ctx.addIssue({
          path: ["confirmPassword"],
          code: z.ZodIssueCode.custom,
          message: "Passwords do not match.",
        });
      }
    }
  });

// --- Defaults ---
const DEFAULT_VALUES = {
  name: "",
  contactNumber: "",
  country: "",
  state: "",
  city: "",
  gender: "",
  nationality: "",
  location: "",
  pic: null,
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const genderOptions = ["Male", "Female", "Other"];

export default function ProfileModal({ showModal, setShowModal, mode }) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  });

  const isAdd = mode === modeConstants?.ADD_MODE;
  const isEdit = mode === modeConstants?.UPDATE_MODE;
  const isView = mode === modeConstants?.VIEW_MODE;
  const dispatch = useDispatch();
  const initialData = useSelector((s) => s.userReducer.profileData);
  const [previewUrl, setPreviewUrl] = useState("");
  const [countryCode, setCountryCode] = useState("pk");

  // Password visibility toggles
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleHide = () => {
    setPreviewUrl("");
    setShowModal(false);
  };

  // File preview
  const picFile = watch("pic");
  useEffect(() => {
    if (!picFile || typeof picFile === "string") return;
    const file = Array.isArray(picFile) ? picFile[0] : picFile;
    if (!(file instanceof File)) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [picFile]);

  // Prefill on open
  useEffect(() => {
    if (!showModal) return;

    if (isAdd) {
      reset(DEFAULT_VALUES);
      setPreviewUrl("");
      return;
    }

    if (initialData) {
      reset({
        name: initialData.name ?? "",
        contactNumber: String(initialData.contactNumber) ?? "",
        country: initialData.country ?? "",
        state: initialData.state ?? "",
        city: initialData.city ?? "",
        gender: initialData.gender ?? "",
        nationality: initialData.nationality ?? "",
        location: initialData.location ?? "",
        pic: null,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      if (initialData?.picUrl) {
        setPreviewUrl(process.env.REACT_APP_STORAGE_URL + initialData?.picUrl);
      } else {
        setPreviewUrl("");
      }
      return;
    }

    reset(DEFAULT_VALUES);
    setPreviewUrl("");
  }, [showModal, isAdd, isEdit, isView, initialData, reset]);

  const onSubmit = (data) => {
    // create FormData instance
    const formData = new FormData();
    // Append profile fields
    formData.append("id", initialData?.id || "");
    formData.append("name", data.name || "");
    formData.append("contactNumber", data.contactNumber || "");
    formData.append("country", data.country || "");
    formData.append("state", data.state || "");
    formData.append("city", data.city || "");
    formData.append("gender", data.gender || "");
    formData.append("nationality", data.nationality || "");
    formData.append("location", data.location || "");

    // Append password fields (only if user wants to change password)
    if (data.currentPassword || data.newPassword) {
      formData.append("currentPassword", data.currentPassword || "");
      formData.append("newPassword", data.newPassword || "");
    }

    // Handle file upload if provided
    let file = data.pic;
    if (file && !(file instanceof File) && file?.[0]) file = file[0];
    if (file instanceof File) {
      formData.append("pic", file, file.name);
    } else {
      formData.append("picUrl", initialData.picUrl);
    }

    dispatch(userActions.updateUserProfile(formData, initialData?.id));
    handleHide();
  };

  const title = "Profile";

  return (
    <Modal
      show={showModal}
      onHide={handleHide}
      size="lg"
      backdrop="static"
      centered
      animation
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={isView}>
            {/* ===== Profile Details ===== */}
            <Card className="mb-3">
              <Card.Header className="fw-semibold">Profile Details</Card.Header>
              <Card.Body>
                <div className="row g-3">
                  <div className="col-lg-6">
                    <div class="form-floating">
                      <input
                        id="name"
                        className="form-control"
                        placeholder="Name"
                        {...register("name", {
                          required: "The field is required",
                        })}
                      />
                      <label for="name">Name</label>
                    </div>
                    {errors.name && (
                      <small className="text-danger">
                        {errors.name.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12 col-lg-6">
                    <Controller
                      name="contactNumber"
                      control={control}
                      rules={{
                        required: "The field is required",
                        validate: (val) =>
                          (val && val.length >= 8) || "Invalid number",
                      }}
                      render={({ field: { value, onChange } }) => (
                        <PhoneInput
                          country={countryCode}
                          value={value.toString()}
                          onChange={(val, country) => {
                            onChange(val);
                            if (country?.countryCode)
                              setCountryCode(country.countryCode);
                          }}
                          inputProps={{
                            name: "contactNumber",
                            required: true,
                            autoFocus: false,
                          }}
                          placeholder="Enter phone number"
                          enableSearch
                          disableSearchIcon={false}
                          inputStyle={{ width: "100%" }}
                          buttonStyle={{
                            borderTopLeftRadius: 6,
                            borderBottomLeftRadius: 6,
                          }}
                          containerStyle={{ width: "100%" }}
                          disabled={isView}
                        />
                      )}
                    />
                    {errors.contactNumber && (
                      <small className="text-danger">
                        {errors.contactNumber.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        id="country"
                        className="form-control"
                        placeholder="Country"
                        {...register("country")}
                      />
                      <label for="country">Country</label>
                    </div>
                    {errors.country && (
                      <small className="text-danger">
                        {errors.country.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        id="state"
                        className="form-control"
                        placeholder="State"
                        {...register("state")}
                      />
                      <label for="state">State</label>
                    </div>
                    {errors.state && (
                      <small className="text-danger">
                        {errors.state.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        id="city"
                        className="form-control"
                        placeholder="City"
                        {...register("city")}
                      />
                      <label for="city">City</label>
                    </div>
                    {errors.city && (
                      <small className="text-danger">
                        {errors.city.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        id="location"
                        className="form-control"
                        placeholder="Location"
                        {...register("location")}
                      />
                      <label for="location">Location</label>
                    </div>
                    {errors.location && (
                      <small className="text-danger">
                        {errors.location.message}
                      </small>
                    )}
                  </div>

                  <div className="col-lg-6">
                    <div class="form-floating">
                      <select
                        className="form-control"
                        id="gender"
                        {...register("gender")}
                      >
                        <option value="">Gender</option>
                        {genderOptions.map((g) => (
                          <option key={g} value={g}>
                            {g}
                          </option>
                        ))}
                      </select>
                      <label for="gender">Gender</label>
                    </div>
                    {errors.gender && (
                      <small className="text-danger">
                        {errors.gender.message}
                      </small>
                    )}
                  </div>

                  <div className="col-lg-6">
                    <div class="form-floating">
                      <input
                        id="nationality"
                        className="form-control"
                        placeholder="Nationality"
                        {...register("nationality")}
                      />
                      <label for="nationality">Nationality</label>
                    </div>
                    {errors.nationality && (
                      <small className="text-danger">
                        {errors.nationality.message}
                      </small>
                    )}
                  </div>

                  {/* Avatar */}
                  <div className="col-12 d-flex gap-3 align-items-center">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 110,
                        height: 110,
                        background: "#eef5ff",
                        overflow: "hidden",
                      }}
                    >
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt="preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <i
                          className="bi bi-person-fill"
                          style={{ fontSize: 56, color: "#2e6bff" }}
                        />
                      )}
                    </div>

                    <div className="flex-grow-1">
                      <label className="form-label">Upload pic</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          setValue("pic", file, { shouldValidate: false });
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* ===== Change Password (separate section) ===== */}
            <Card>
              <Card.Header className="fw-semibold">Change Password</Card.Header>
              <Card.Body>
                <div className="row g-3">
                  {/* Current Password */}
                  <div className="col-12 col-lg-4">
                    <div className="input-group">
                      <div class="form-floating">
                        <input
                          id="currentPassword"
                          type={showCurrent ? "text" : "password"}
                          className="form-control"
                          placeholder="Current Password"
                          autoComplete="current-password"
                          {...register("currentPassword")}
                        />
                        <label for="currentPassword">Current Password</label>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowCurrent((v) => !v)}
                        title={showCurrent ? "Hide password" : "Show password"}
                      >
                        <i
                          className={
                            showCurrent ? "ri-eye-off-line" : "ri-eye-line"
                          }
                        />
                      </button>
                    </div>
                    {errors.currentPassword && (
                      <small className="text-danger">
                        {errors.currentPassword.message}
                      </small>
                    )}
                  </div>

                  {/* New Password */}
                  <div className="col-12 col-lg-4">
                    <div className="input-group">
                      <div class="form-floating">
                        <input
                          id="newPassword"
                          type={showNew ? "text" : "password"}
                          className="form-control"
                          placeholder="New Password"
                          autoComplete="new-password"
                          {...register("newPassword")}
                        />
                        <label for="newPassword">New Password</label>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowNew((v) => !v)}
                        title={showNew ? "Hide password" : "Show password"}
                      >
                        <i
                          className={
                            showNew ? "ri-eye-off-line" : "ri-eye-line"
                          }
                        />
                      </button>
                    </div>
                    {errors.newPassword && (
                      <small className="text-danger">
                        {errors.newPassword.message}
                      </small>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="col-12 col-lg-4">
                    <div className="input-group">
                      <div class="form-floating">
                        <input
                          id="confirmPassword"
                          type={showConfirm ? "text" : "password"}
                          className="form-control"
                          placeholder="Confirm New Password"
                          autoComplete="new-password"
                          {...register("confirmPassword")}
                        />
                        <label for="confirmPassword">Confirm Password</label>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowConfirm((v) => !v)}
                        title={showConfirm ? "Hide password" : "Show password"}
                      >
                        <i
                          className={
                            showConfirm ? "ri-eye-off-line" : "ri-eye-line"
                          }
                        />
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <small className="text-danger">
                        {errors.confirmPassword.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12">
                    <small className="text-muted">
                      Leave password fields blank if you don’t want to change
                      your password.
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </fieldset>

          {/* Actions */}
          <div className="d-flex justify-content-end gap-2 footer-modal">
            <Button
              variant="outline-secondary"
              onClick={handleHide}
              className="btn-footer"
            >
              {isView ? "Close" : "Cancel"}
            </Button>
            {!isView && (
              <Button type="submit" variant="success" className="btn-footer">
                Save Changes
              </Button>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
